import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import { cn } from "../../utils/classMerger";
import { formatUsDateTime } from "../../utils/dateFormat";
import {
  getZapCapBalance,
  getZapCapUserUsage,
  refreshZapCapBalance,
  updateZapCapAlertConfig,
} from "./actions";
import type { ZapCapBalance, ZapCapUserUsage } from "./types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/misc/Table";

const THRESHOLD_INPUT_ID = "zapcap-threshold-input";
const THRESHOLD_PATTERN = /^\d+(\.\d{1,2})?$/;
const MIN_THRESHOLD_USD = 0.01;

type ChipState = "unknown" | "ok" | "below";

const CHIP_STYLE: Record<ChipState, string> = {
  unknown: "bg-[#bfbfbf]/30 text-[#193456]",
  ok: "bg-[#beeb9f] text-[#43a047]",
  below: "bg-[#ff5252]/20 text-[#b9121b]",
};

const CHIP_LABEL: Record<ChipState, string> = {
  unknown: "Never checked",
  ok: "OK",
  below: "Below threshold",
};

const formatUsd = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : `$${n.toFixed(2)}`;


const extractApiError = (err: unknown, fallback: string): string => {
  const axErr = err as AxiosError<{ message?: string | string[] }>;
  const msg = axErr?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  return msg ?? fallback;
};

const ZapCapTab: React.FC = () => {
  const [data, setData] = useState<ZapCapBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [thresholdInput, setThresholdInput] = useState<string>("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [userUsage, setUserUsage] = useState<ZapCapUserUsage[] | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const toggleUserExpansion = (userId: string): void => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getZapCapUserUsage();
        if (!cancelled) setUserUsage(result.users);
      } catch (err) {
        if (cancelled) return;
        const message = extractApiError(err, "Failed to load ZapCap user usage");
        setUsageError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyData = (next: ZapCapBalance): void => {
    setData(next);
    setThresholdInput(next.thresholdUsd.toString());
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getZapCapBalance();
        if (!cancelled) applyData(result);
      } catch (err) {
        if (!cancelled) {
          toast.error(extractApiError(err, "Failed to load ZapCap balance"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRefresh = async (): Promise<void> => {
    if (refreshing || savingThreshold) return;
    setRefreshing(true);
    try {
      const result = await refreshZapCapBalance();
      applyData(result);
      toast.success("ZapCap balance refreshed");
    } catch (err) {
      toast.error(extractApiError(err, "Failed to refresh ZapCap balance"));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveThreshold = async (): Promise<void> => {
    if (savingThreshold || refreshing) return;
    if (!THRESHOLD_PATTERN.test(thresholdInput)) {
      toast.error("Threshold must be a positive number with up to 2 decimals");
      return;
    }
    const parsed = parseFloat(thresholdInput);
    if (!Number.isFinite(parsed) || parsed < MIN_THRESHOLD_USD) {
      toast.error(`Threshold must be at least $${MIN_THRESHOLD_USD.toFixed(2)}`);
      return;
    }
    setSavingThreshold(true);
    try {
      const result = await updateZapCapAlertConfig({ thresholdUsd: parsed });
      applyData(result);
      toast.success("Alert threshold updated");
    } catch (err) {
      toast.error(extractApiError(err, "Failed to update alert threshold"));
    } finally {
      setSavingThreshold(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading ZapCap balance...
      </div>
    );
  }

  if (!data) {
    return <div className="text-sm text-[#b9121b]">No data available.</div>;
  }

  const chipState: ChipState =
    data.balance === null
      ? "unknown"
      : data.balance < data.thresholdUsd
        ? "below"
        : "ok";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg">Current Balance</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || savingThreshold}
            >
              <RefreshCw
                className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")}
              />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {formatUsd(data.balance)}
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              Last checked: {formatUsDateTime(data.lastCheckedAt)}
            </div>
            {data.balance === null && (
              <div className="mt-1 text-xs text-muted-foreground">
                No balance fetched yet. Click Refresh to fetch the latest.
              </div>
            )}
            <div className="absolute bottom-2 right-2 z-20">
              <span
                className={cn(
                  "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
                  CHIP_STYLE[chipState],
                )}
              >
                {CHIP_LABEL[chipState]}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Low-Balance Alert</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1">
              <label
                htmlFor={THRESHOLD_INPUT_ID}
                className="text-sm font-medium text-primary"
              >
                Threshold (USD)
              </label>
              <input
                id={THRESHOLD_INPUT_ID}
                type="number"
                step="0.01"
                min={MIN_THRESHOLD_USD}
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                className={cn(
                  "w-32 px-3 py-2 border rounded-md text-sm",
                  "focus:outline-none focus:ring-2 focus:ring-primary",
                )}
              />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                onClick={handleSaveThreshold}
                disabled={savingThreshold || refreshing}
              >
                {savingThreshold ? "Saving..." : "Save"}
              </Button>
            </div>
            <div className="mt-4 text-xs text-muted-foreground">
              An alert email is sent to the admin every time a balance check
              finds the balance below this threshold.
              {data.lastAlertSentAt && (
                <div className="mt-1">
                  Last alert sent: {formatUsDateTime(data.lastAlertSentAt)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">User Usage</CardTitle>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <div className="text-sm text-gray-500">Loading user usage...</div>
          ) : usageError ? (
            <div className="text-sm text-[#b9121b]">{usageError}</div>
          ) : !userUsage || userUsage.length === 0 ? (
            <div className="text-sm text-gray-500">
              No captioning usage recorded yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="max-[900px]:hidden">Email</TableHead>
                  <TableHead className="text-right">
                    Cycle Used (min)
                  </TableHead>
                  <TableHead className="text-right">Limit (min)</TableHead>
                  <TableHead className="text-right">% Used</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userUsage.map((user) => {
                  const isExpanded = expandedUsers.has(user.userId);
                  const pct =
                    user.secondsLimit > 0
                      ? Math.round(
                          (user.cycleSecondsUsed / user.secondsLimit) * 100,
                        )
                      : 0;
                  const name =
                    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    null;
                  return (
                    <React.Fragment key={user.userId}>
                      <TableRow
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleUserExpansion(user.userId)}
                      >
                        <TableCell>
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {name ?? (
                            <span className="text-gray-500">
                              {user.deleted ? "Deleted user" : "Unnamed user"}{" "}
                              <span className="font-mono text-xs">
                                {user.userId}
                              </span>
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="max-[900px]:hidden">
                          {user.email || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {(user.cycleSecondsUsed / 60).toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right">
                          {(user.secondsLimit / 60).toFixed(0)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-medium",
                            pct >= 90
                              ? "text-[#b9121b]"
                              : pct >= 70
                                ? "text-[#fa9600]"
                                : "text-[#43a047]",
                          )}
                        >
                          {pct}%
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} className="p-0">
                            <div className="bg-gray-50 p-4">
                              <div className="mb-2 text-xs text-gray-500">
                                {(user.totalSeconds / 60).toFixed(1)} min
                                captioned all-time
                              </div>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Month</TableHead>
                                    <TableHead className="text-right">
                                      Minutes
                                    </TableHead>
                                    <TableHead className="text-right">
                                      Captioned Videos
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {user.monthlyData.map((month) => (
                                    <TableRow
                                      key={`${month.year}-${month.month}`}
                                    >
                                      <TableCell>
                                        {month.year}-
                                        {String(month.month).padStart(2, "0")}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {(month.seconds / 60).toFixed(1)}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {month.videos}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ZapCapTab;
